import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../app/Store";
import { LoadingState } from "../app/LoadingState";
import { beginRequest, completeRequest, rejectRequest, RequestState } from "../app/store/RequestState";
import { ActiveConfigInfo, ConfigNameAndDescription, SiteAppModule } from "./AppConfigModels";
import { getActiveConfigInfoAsync, getConfigListAsync } from "./AppConfigAPI";
import { getSiteModuleMetadata } from "./moduleMetadata/ModuleMetadataApi";
import { IModuleMetadata } from "./moduleMetadata/models";
import { getDeploymentVersions } from "./SiteAppsDeployApi";
import { getFirmwareVersions } from "./tcmAutomaticUpload/TcmAutomaticFwUploadApi";

export interface ConfigurationArgs {
  siteId: string;
  siteApp: SiteAppModule;
  configVersion: string;
}
export const configurationKey = (args: ConfigurationArgs) =>
  JSON.stringify([args.siteId, args.siteApp, args.configVersion]);
interface ConfigurationData {
  list: ConfigNameAndDescription[];
  active: ActiveConfigInfo | null;
  activeError?: string;
}
interface DataEntry<T> extends RequestState {
  data?: T;
}
const initialState: {
  configurations: Record<string, DataEntry<ConfigurationData>>;
  modules: Record<string, DataEntry<IModuleMetadata[]>>;
  deploymentVersions: Record<string, DataEntry<string[]>>;
  firmwareVersions: DataEntry<string[]>;
} = {
  configurations: {},
  modules: {},
  deploymentVersions: {},
  firmwareVersions: { loadingState: LoadingState.None },
};
export const fetchDeploymentVersions = createAsyncThunk(
  "siteConfigurationData/deploymentVersions/fetch",
  (tagName: string, { signal }) => getDeploymentVersions(tagName, signal),
  {
    condition: (tagName, { getState }) =>
      (getState() as RootState).siteConfigurationData.deploymentVersions[tagName]?.loadingState !==
      LoadingState.Pending,
  }
);
export const fetchFirmwareVersions = createAsyncThunk(
  "siteConfigurationData/firmwareVersions/fetch",
  (_, { signal }) => getFirmwareVersions(signal),
  {
    condition: (_, { getState }) =>
      (getState() as RootState).siteConfigurationData.firmwareVersions.loadingState !==
      LoadingState.Pending,
  }
);
export const fetchModuleMetadata = createAsyncThunk(
  "siteConfigurationData/modules/fetch",
  (siteId: string, { signal }) => getSiteModuleMetadata(siteId, signal),
  {
    condition: (siteId, { getState }) =>
      Boolean(siteId) &&
      (getState() as RootState).siteConfigurationData.modules[siteId]?.loadingState !==
        LoadingState.Pending,
  }
);
export const fetchConfigurationList = createAsyncThunk(
  "siteConfigurationData/configurations/fetch",
  async (args: ConfigurationArgs, { signal }) => {
    let activeError: string | undefined;
    const [list, active] = await Promise.all([
      getConfigListAsync(args.siteId, args.configVersion, args.siteApp, signal),
      getActiveConfigInfoAsync(args.siteId, args.siteApp, signal).catch((error: unknown) => {
        if (signal.aborted) {
          throw error;
        }
        activeError = error instanceof Error ? error.message : "Failed to get active version";
        return null;
      }),
    ]);
    return { list, active, activeError };
  },
  {
    condition: (args, { getState }) =>
      Boolean(args.siteId) &&
      (getState() as RootState).siteConfigurationData.configurations[configurationKey(args)]
        ?.loadingState !== LoadingState.Pending,
  }
);
const slice = createSlice({
  name: "siteConfigurationData",
  initialState,
  reducers: {
    invalidateModuleMetadata: (state, action: PayloadAction<string>) => {
      delete state.modules[action.payload];
    },
    invalidateConfigurationList: (
      state,
      action: PayloadAction<{ siteId: string; siteApp: SiteAppModule }>
    ) => {
      for (const key of Object.keys(state.configurations)) {
        const [siteId, siteApp] = JSON.parse(key) as string[];
        if (siteId === action.payload.siteId && siteApp === action.payload.siteApp) {
          const data = state.configurations[key].data;
          if (data) {
            // Keep the last good list while invalidating ownership of pre-mutation reads.
            state.configurations[key] = { data, loadingState: LoadingState.None };
          } else {
            delete state.configurations[key];
          }
        }
      }
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchConfigurationList.pending, (state, action) => {
        beginRequest(
          (state.configurations[configurationKey(action.meta.arg)] ??= {
            loadingState: LoadingState.None,
          }),
          action.meta.requestId
        );
      })
      .addCase(fetchConfigurationList.fulfilled, (state, action) => {
        const entry = state.configurations[configurationKey(action.meta.arg)];
        if (entry && completeRequest(entry, action.meta.requestId)) {
          entry.data = action.payload;
        }
      })
      .addCase(fetchConfigurationList.rejected, (state, action) => {
        const entry = state.configurations[configurationKey(action.meta.arg)];
        if (entry) {
          rejectRequest(entry, action);
        }
      })
      .addCase(fetchModuleMetadata.pending, (state, action) => {
        beginRequest(
          (state.modules[action.meta.arg] ??= { loadingState: LoadingState.None }),
          action.meta.requestId
        );
      })
      .addCase(fetchModuleMetadata.fulfilled, (state, action) => {
        const entry = state.modules[action.meta.arg];
        if (entry && completeRequest(entry, action.meta.requestId)) {
          entry.data = action.payload;
        }
      })
      .addCase(fetchModuleMetadata.rejected, (state, action) => {
        const entry = state.modules[action.meta.arg];
        if (entry) {
          rejectRequest(entry, action);
        }
      })
      .addCase(fetchDeploymentVersions.pending, (state, action) => {
        beginRequest(
          (state.deploymentVersions[action.meta.arg] ??= { loadingState: LoadingState.None }),
          action.meta.requestId
        );
      })
      .addCase(fetchDeploymentVersions.fulfilled, (state, action) => {
        const entry = state.deploymentVersions[action.meta.arg];
        if (entry && completeRequest(entry, action.meta.requestId)) {
          entry.data = action.payload;
        }
      })
      .addCase(fetchDeploymentVersions.rejected, (state, action) => {
        const entry = state.deploymentVersions[action.meta.arg];
        if (entry) {
          rejectRequest(entry, action);
        }
      })
      .addCase(fetchFirmwareVersions.pending, (state, action) => {
        beginRequest(state.firmwareVersions, action.meta.requestId);
      })
      .addCase(fetchFirmwareVersions.fulfilled, (state, action) => {
        if (completeRequest(state.firmwareVersions, action.meta.requestId)) {
          state.firmwareVersions.data = action.payload;
        }
      })
      .addCase(fetchFirmwareVersions.rejected, (state, action) => {
        rejectRequest(state.firmwareVersions, action);
      }),
});
export const { invalidateConfigurationList, invalidateModuleMetadata } = slice.actions;
export const siteConfigurationDataReducer = slice.reducer;
